import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { useTooltip, Button, Icon, Text } from '@frontend/design-system';
import { SimpleTimelineBar } from './timeline-chart';
import { TimelineBarProps } from './types';

export const PositiveTimelineBar = ({
  label,
  width,
  details,
  onClick,
}: Omit<TimelineBarProps, 'background' | 'outlineColor'>) => {
  return (
    <SimpleTimelineBar
      label={label}
      width={width}
      background={theme.colors.success20}
      details={details}
      onClick={onClick}
    />
  );
};

export const NegativeTimelineBar = ({
  label,
  width,
  details,
  onClick,
}: Omit<TimelineBarProps, 'background' | 'outlineColor'>) => {
  const { t } = useTranslation('calls');
  const {
    tooltipProps: iconTooltipProps,
    triggerProps: iconTriggerProps,
    Tooltip: IconTooltip,
  } = useTooltip({
    trigger: 'hover',
    theme: 'light',
    showArrow: false,
  });

  return (
    <div
      css={{
        width,
        position: 'relative',
      }}
    >
      <Button
        iconName='alert-small'
        variant='secondary'
        {...iconTriggerProps}
        css={{ position: 'absolute', top: -16, left: -8, width: 16, height: 16, padding: 0 }}
      />
      <SimpleTimelineBar
        label={label}
        width={'auto'}
        background={theme.colors.critical30}
        outlineColor={theme.colors.critical50}
        details={details}
        onClick={onClick}
      />
      <IconTooltip
        {...iconTooltipProps}
        css={{
          borderRadius: theme.borderRadius.large,
          width: 320,
          maxWidth: 320,
          padding: 0,
        }}
      >
        <div
          css={{
            display: 'grid',
            gap: theme.spacing(1),
            padding: theme.spacing(3),
            fontWeight: 'normal',
          }}
        >
          <Text as='span' size='small'>
            {label}
          </Text>
          <div css={{ borderTop: `1px solid ${theme.colors.neutral10}` }} />

          <div
            css={{
              display: 'grid',
              gap: theme.spacing(1),
              gridTemplateColumns: 'max-content max-content',
              alignItems: 'center',
            }}
          >
            <Icon name='alert-small' color='warn' />
            <Text as='span' size='medium' weight='bold'>
              {t('Quality Alert Sent')}
            </Text>
          </div>
          <Text size='medium'>
            {t(
              'The system sends quality alerts when the Call Metrics drop below the thresholds for stable connections.'
            )}
          </Text>
        </div>
      </IconTooltip>
    </div>
  );
};

export const barTypes = {
  positive: PositiveTimelineBar,
  negative: NegativeTimelineBar,
} as const;
